import styled, { css } from 'styled-components';
import { convertToPixel } from './utils';
import { lightColors } from './colors';

interface SkeletonProps {
  variant?: 'default' | 'outline';
  height?: number | string;
  width?: number | string;
  rounded?: boolean;
  background?: string;
}

const variantStyles = ({ background }: SkeletonProps) => ({
  default: css`
    background-color: ${background || '#f7f7f7'};
  `,
  outline: css`
    background-color: transparent;
    border: 1px solid #f7f7f7;
  `,
});

export const Skeleton = styled.div<SkeletonProps>`
  ${({ variant = 'default', ...props }) => variantStyles(props)[variant]}
  height: ${({ height }) => convertToPixel(height)};
  width: ${({ width = 24 }) => convertToPixel(width)};
  border-radius: ${({ rounded, width, height }) => {
    if (typeof width === 'number') {
      return rounded ? convertToPixel(width / 2) : '6px';
    }
    if (typeof height === 'number') {
      return rounded ? convertToPixel(height / 2) : '6px';
    }

    return rounded ? '50%' : '6px';
  }};
`;

export const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${lightColors.modals.border};
`;

export const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;

  margin-bottom: 8px;

  background-color: ${lightColors.modals.surfaceDefault};
`;

export const ContentGrid = styled.div`
  overflow: hidden;
  flex: 1;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 8px;
  max-width: 100%;
  position: relative;

  @media only screen and (min-width: 728px) {
    grid-template-columns: 1fr 1fr;
  }

  @media only screen and (min-width: 1048px) {
    grid-template-columns: 680px 1fr;
  }
`;

export const LeftSection = styled.div`
  overflow: hidden;
  background-color: ${lightColors.modals.surfaceDefault};
`;

export const RightSection = styled.div`
  overflow: hidden;
  display: none;
  background-color: ${lightColors.modals.surfaceDefault};

  @media only screen and (min-width: 728px) {
    display: flex;
  }
`;

export const PortfolioContainer = styled.div`
  display: flex;
  flex-direction: column;

  row-gap: 10px;

  border-radius: 4px;
  border: 1px solid ${lightColors.modals.surfaceEmphasis};
  padding: 12px;
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${lightColors.modals.surfaceEmphasis};
`;

export const ThickDivider = styled.div`
  width: 100%;
  height: 8px;
  background-color: #f6f6f6;
`;

export const TableRow = styled.div`
  position: relative;

  display: flex;
  align-items: stretch;
  gap: 1px;
  width: 100%;
`;

export const TableCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20%;
  padding: 5px;
  background-color: ${lightColors.modals.surfaceDefault};
`;

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 1px;
  width: 100%;
  text-align: center;
  background-color: ${lightColors.modals.surfaceEmphasis};

  border-radius: 4px;

  & > ${TableRow}:first-of-type {
    > ${TableCell}:first-of-type {
      border-top-left-radius: 4px;
    }

    > ${TableCell}:last-of-type {
      border-top-right-radius: 4px;
    }
  }

  & > ${TableRow}:last-of-type {
    > ${TableCell}:first-of-type {
      border-bottom-left-radius: 4px;
    }

    > ${TableCell}:last-of-type {
      border-bottom-right-radius: 4px;
    }
  }
`;

export const BorderedButton = styled.div`
  width: 80px;
  height: 28px;
  border: 1px solid #f7f7f7;
  border-radius: 14px;
`;
